import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { formatDate, formatKrw, formatNum } from '../lib/format';

const PAGE_SIZE = 50;

export function Users() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(0);

  // 200ms 디바운스
  useDebouncedEffect(() => {
    setDebouncedQ(q.trim());
    setPage(0);
  }, [q], 200);

  const offset = page * PAGE_SIZE;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'users', debouncedQ, offset],
    queryFn: () => adminApi.listUsers({ q: debouncedQ || undefined, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">회원</h1>
          <p className="text-sm text-text-muted mt-1">
            {isLoading ? '로딩 중…' : `${formatNum(total)}명`}
          </p>
        </div>
        <input
          type="search"
          className="input max-w-xs"
          placeholder="전화 / 이름 / 이메일 / 회사명 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </header>

      {isError && (
        <div className="card text-sm text-error">
          불러오기 실패: {(error as { error?: string })?.error ?? String(error)}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-elev text-text-secondary">
            <tr>
              <Th>이름</Th>
              <Th>회사</Th>
              <Th>전화</Th>
              <Th>가입</Th>
              <Th className="text-right">이번달 호출</Th>
              <Th className="text-right">이번달 비용</Th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                  결과 없음
                </td>
              </tr>
            )}
            {data?.items.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-surface-elev">
                <Td>
                  <Link to={`/users/${u.id}`} className="text-accent hover:underline">
                    {u.name ?? '(이름 없음)'}
                  </Link>
                </Td>
                <Td className="text-text-secondary">{u.company_name ?? '—'}</Td>
                <Td className="text-text-secondary font-mono text-xs">{u.phone_e164}</Td>
                <Td className="text-text-muted text-xs">{formatDate(u.created_at)}</Td>
                <Td className="text-right text-text-secondary">{formatNum(u.this_month_events)}</Td>
                <Td className="text-right text-text-primary font-medium">
                  {formatKrw(u.this_month_cost, true)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-text-muted">
        <div>
          {formatNum(offset + 1)} – {formatNum(Math.min(offset + PAGE_SIZE, total))} / {formatNum(total)}
        </div>
        <div className="flex gap-2">
          <button
            className="btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            이전
          </button>
          <button
            className="btn-ghost"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${className ?? ''}`}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-middle ${className ?? ''}`}>{children}</td>;
}

// react 18 — 작은 인라인 debounce helper
import { useEffect } from 'react';
function useDebouncedEffect(fn: () => void, deps: unknown[], delay: number): void {
  useEffect(() => {
    const t = setTimeout(fn, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
